import { CubeReducerState } from 'modules/Cube/domain/cube.domain.types'
import { getAutoIssueInvoicesAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getAutoIssueInvoicesAvailable'
import { getLabelAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getLabelAvailable'
import { getPublishAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getPublishAvailable'
import { getPurchaseOrderNumberAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getPurchaseOrderNumberAvailable'
import { getRecurrenceDateAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getRecurrenceDateAvailable'
import { getReferenceAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getReferenceAvailable'
import { getRollUpBillingAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getRollUpBillingAvailable'
import { getSaveAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getSaveAvailable'
import { getStripePaymentAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getStripePaymentAvailable'
import { getTaxRateAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getTaxRateAvailable'
import { getStripeAutoChargeAvailable } from 'Cube/domain/postActionStages/queries/availableFeatures/schedule/getStripeAutoChargeAvailable.ts'
import { getGoCardlessPaymentAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getGoCardlessPaymentAvailable'
import { getDefaultDueDateDaysAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getDefaultDueDateDaysAvailable'
import { getMemoAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/schedule/getMemoAvailable'
import compose from 'lodash/fp/compose'
import { disableIfLockedByAmendment } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/utils/amendmentLock'

export const getScheduleFeaturesAvailable = (
  prevState: CubeReducerState
): CubeReducerState['queries']['availableFeatures']['schedule'] => {
  /**
   * Each feature composes the 'global' rules that hold for it. Today that is
   * just the amendment lock, and it holds for every feature here: a locked
   * schedule can't be saved, so anything editable on one is an edit that goes
   * nowhere.
   *
   * Not every global rule is modelled this way yet: plenty still sit inside
   * individual features, repeated. A future refactor would lift the common
   * ones out to here, which is why each feature gets its own compose rather
   * than sharing one pipeline.
   */
  const lockedByAmendment = disableIfLockedByAmendment(prevState)

  return {
    stripePayment: compose(lockedByAmendment)(
      getStripePaymentAvailable(prevState)
    ),
    stripeAutoCharge: compose(lockedByAmendment)(
      getStripeAutoChargeAvailable(prevState)
    ),
    goCardlessPayment: compose(lockedByAmendment)(
      getGoCardlessPaymentAvailable(prevState)
    ),
    rollUpBilling: compose(lockedByAmendment)(
      getRollUpBillingAvailable(prevState)
    ),
    publish: compose(lockedByAmendment)(getPublishAvailable(prevState)),
    save: compose(lockedByAmendment)(getSaveAvailable(prevState)),
    recurrenceDayOfMonth: compose(lockedByAmendment)(
      getRecurrenceDateAvailable(prevState)
    ),
    taxRate: compose(lockedByAmendment)(getTaxRateAvailable(prevState)),
    label: compose(lockedByAmendment)(getLabelAvailable(prevState)),
    reference: compose(lockedByAmendment)(getReferenceAvailable(prevState)),
    purchaseOrderNumber: compose(lockedByAmendment)(
      getPurchaseOrderNumberAvailable(prevState)
    ),
    autoIssueInvoices: compose(lockedByAmendment)(
      getAutoIssueInvoicesAvailable(prevState)
    ),
    /**
     * These two have no rules of their own at all - they are enabled whatever the schedule is
     * doing. That is why they were left out here at first, on the grounds that a cancelled
     * schedule allows them too. But the save is locked, so an edit to either just disappears,
     * which is worse than not offering it.
     */
    defaultDueDateDays: compose(lockedByAmendment)(
      getDefaultDueDateDaysAvailable(prevState)
    ),
    memo: compose(lockedByAmendment)(getMemoAvailable(prevState))
  }
}
